import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import './NeighborhoodPage.css';

const NEIGHBORHOODS = {
  flint: {
    name: 'Flint',
    state: 'MI',
    city: 'Flint',
    zip: '48502',
    image: 'https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/tulsahome_tile.jpg',
    heroSubtitle: 'The Heart of Genesee County',
    description: 'Flint is a city rich in history and resilience, offering some of the most affordable real estate in Michigan. From the revitalized downtown corridor along Saginaw Street to the tree-lined neighborhoods of College Cultural and Mott Park, Flint presents exceptional opportunities for buyers seeking value and character. The city is home to the Flint Cultural Center, Kettering University, and the University of Michigan–Flint, making it a hub for education and culture.',
    sellingPoints: [
      'Median home prices well below the national average — ideal for first-time buyers and investors',
      'Ongoing downtown revitalization with new restaurants, breweries, and mixed-use developments',
      'Proximity to I-69 and I-75 for easy commuting to Detroit, Lansing, and Saginaw',
      'Strong rental demand driven by university students and healthcare professionals'
    ],
    amenities: [
      { title: 'Flint Cultural Center', desc: 'Home to the Sloan Museum, Flint Institute of Arts, Longway Planetarium, and the Whiting Auditorium — one of the largest cultural complexes in the Midwest.' },
      { title: 'University of Michigan–Flint', desc: 'A growing campus that anchors the downtown area and drives demand for nearby housing.' },
      { title: 'Chevy Commons & Riverbank Park', desc: 'Green spaces along the Flint River offering walking trails, kayaking, and community events.' },
      { title: 'Hurley Medical Center', desc: 'A major regional employer and Level I trauma center, supporting a strong healthcare workforce in the area.' }
    ],
    marketStats: { medianPrice: '$85,000', daysOnMarket: '35', popularTypes: 'Single Family, Multi-Family, Investment Properties' },
    buyingContent: 'Whether you are purchasing your first home or expanding an investment portfolio, Flint offers unmatched affordability in the Michigan market. Many neighborhoods feature classic brick bungalows, Craftsman-style homes, and spacious colonials on generous lots. David Hamady provides expert guidance on navigating Flint\'s diverse housing stock, from move-in ready properties to renovation opportunities with significant upside potential.',
    sellingContent: 'Selling a home in Flint requires a strategic approach that highlights the unique value of your property in a competitive market. David Hamady leverages professional photography, targeted digital marketing, and deep local market knowledge to position your listing for maximum exposure. With investor interest and first-time buyer demand on the rise, now is an excellent time to list your Flint property.'
  },
  'grand-blanc': {
    name: 'Grand Blanc',
    state: 'MI',
    zip: '48439',
    image: 'https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/bixby_landing_tile.jpeg',
    heroSubtitle: 'Premier Suburban Living in Genesee County',
    description: 'Grand Blanc is consistently ranked among the best places to live in Michigan, known for its top-rated school district, safe neighborhoods, and thriving commercial corridor along Hill Road and Holly Road. Families are drawn to Grand Blanc for its excellent schools, while professionals appreciate the easy commute to Flint, Detroit, and the surrounding metro area. The community offers a perfect balance of suburban tranquility and modern convenience.',
    sellingPoints: [
      'Grand Blanc Community Schools — one of the highest-rated districts in Genesee County',
      'Strong property values with consistent year-over-year appreciation',
      'Vibrant shopping and dining along the Hill Road corridor',
      'Minutes from Genesys Regional Medical Center and major employers'
    ],
    amenities: [
      { title: 'Grand Blanc Community Schools', desc: 'Award-winning K-12 district with advanced placement programs, competitive athletics, and modern facilities.' },
      { title: 'Genesys Regional Medical Center', desc: 'Ascension Genesys is a major healthcare campus and one of the largest employers in the area.' },
      { title: 'Creasey Bicentennial Park', desc: 'A 164-acre park featuring walking trails, sports fields, a dog park, and the Grand Blanc Senior Center.' },
      { title: 'Shopping & Dining', desc: 'The Hill Road corridor offers national retailers, local boutiques, and a diverse restaurant scene.' }
    ],
    marketStats: { medianPrice: '$265,000', daysOnMarket: '22', popularTypes: 'Single Family, New Construction, Condominiums' },
    buyingContent: 'Grand Blanc attracts buyers who want top-tier schools, safe neighborhoods, and strong home values. The housing market here features everything from starter homes in established subdivisions to luxury new construction in gated communities. David Hamady helps buyers navigate this competitive market, providing early access to listings and expert negotiation to secure the best possible terms.',
    sellingContent: 'Homes in Grand Blanc sell quickly when priced and marketed correctly. The combination of school district reputation and community amenities creates consistent buyer demand. David Hamady uses comprehensive market analysis, professional staging consultation, and multi-channel digital marketing to ensure your Grand Blanc home attracts qualified buyers and achieves top dollar.'
  },
  davison: {
    name: 'Davison',
    state: 'MI',
    zip: '48423',
    image: 'https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic_flint_home_003.jpeg',
    heroSubtitle: 'Small-Town Charm with Big Opportunity',
    description: 'Davison is a welcoming community east of Flint that offers the best of small-town Michigan living. Known for its charming downtown, strong school system, and annual Davison Farmers Market, this city attracts families and retirees alike. The housing market features well-maintained homes on spacious lots, many with acreage, providing a rural feel with convenient access to urban amenities along M-15 and I-69.',
    sellingPoints: [
      'Davison Community Schools — a respected district with strong academics and athletics',
      'Affordable home prices with larger lot sizes compared to neighboring communities',
      'Charming downtown with local shops, restaurants, and community events',
      'Quick access to I-69 for commuting to Flint, Lapeer, and Port Huron'
    ],
    amenities: [
      { title: 'Davison Community Schools', desc: 'A well-regarded district known for academic excellence and a strong sense of community involvement.' },
      { title: 'Davison Farmers Market', desc: 'A seasonal market that brings the community together with local produce, crafts, and live entertainment.' },
      { title: 'Davison Country Club', desc: 'An 18-hole golf course and social club that serves as a centerpiece of the community.' },
      { title: 'Abigail Park & Recreation', desc: 'Parks and recreation facilities offering youth sports, walking trails, and seasonal activities.' }
    ],
    marketStats: { medianPrice: '$195,000', daysOnMarket: '28', popularTypes: 'Single Family, Ranch, Homes with Acreage' },
    buyingContent: 'Davison offers buyers an exceptional value proposition — quality homes in a strong school district at prices below the metro average. Whether you are looking for a move-in ready ranch, a colonial with acreage, or a starter home near downtown, David Hamady will help you find the right fit. His deep knowledge of the Davison market ensures you make an informed purchase.',
    sellingContent: 'The Davison market benefits from steady demand driven by families seeking quality schools and a tight-knit community. David Hamady positions your home to stand out with professional photography, compelling listing descriptions, and targeted outreach to qualified buyers actively searching in the Davison area.'
  },
  burton: {
    name: 'Burton',
    state: 'MI',
    zip: '48509',
    image: 'https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic_flint_home_001.jpeg',
    heroSubtitle: 'Affordable Living in the Heart of Genesee County',
    description: 'Burton is the second-largest city in Genesee County and offers some of the most accessible homeownership opportunities in the region. Strategically located along I-69 and Court Street, Burton provides easy access to Flint, Grand Blanc, and the broader metro area. The city features a mix of established residential neighborhoods, commercial corridors, and green spaces, making it an attractive option for first-time buyers, families, and investors.',
    sellingPoints: [
      'Highly affordable housing with strong potential for appreciation',
      'Central location with direct access to I-69, I-75, and major commercial corridors',
      'Bentley and Atherton school districts serving the community',
      'Growing commercial development along Court Street and Dort Highway'
    ],
    amenities: [
      { title: 'For-Mar Nature Preserve', desc: 'A 383-acre nature preserve operated by Genesee County Parks, offering trails, wildlife viewing, and educational programs.' },
      { title: 'Courtland Center Mall Area', desc: 'A commercial hub with retail, dining, and service businesses serving the Burton community.' },
      { title: 'Burton Community Recreation', desc: 'City parks, sports leagues, and community programs for residents of all ages.' },
      { title: 'Strategic Location', desc: 'Positioned at the crossroads of I-69 and Dort Highway, Burton offers convenient access to employment centers throughout Genesee County.' }
    ],
    marketStats: { medianPrice: '$120,000', daysOnMarket: '30', popularTypes: 'Single Family, Ranch, Starter Homes' },
    buyingContent: 'Burton represents one of the best entry points into Genesee County homeownership. With median prices well below the state average, buyers can find solid, well-built homes in established neighborhoods. David Hamady helps buyers identify properties with the best value, whether you are looking for a turnkey home or a property with renovation potential and strong return on investment.',
    sellingContent: 'Burton\'s affordability drives consistent buyer interest, particularly from first-time homeowners and investors. David Hamady ensures your Burton property is marketed effectively with professional visuals, accurate pricing, and broad digital exposure to reach the right buyers at the right time.'
  },
  'swartz-creek': {
    name: 'Swartz Creek',
    state: 'MI',
    zip: '48473',
    image: 'https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/owasso_landing_tile.webp',
    heroSubtitle: 'A Welcoming Community West of Flint',
    description: 'Swartz Creek is a family-oriented community located just west of Flint along Miller Road and I-69. Known for its strong school district and friendly neighborhoods, Swartz Creek offers a quieter pace of life while maintaining convenient access to shopping, dining, and employment throughout the Flint metro area. The housing market features a range of options from affordable starter homes to spacious properties in well-established subdivisions.',
    sellingPoints: [
      'Swartz Creek Community Schools — a district known for academic quality and community engagement',
      'Affordable homes with well-maintained neighborhoods and mature tree-lined streets',
      'Convenient access to I-69 and the Miller Road commercial corridor',
      'Active community events including the annual Swartz Creek Heritage Festival'
    ],
    amenities: [
      { title: 'Swartz Creek Community Schools', desc: 'A respected district offering comprehensive K-12 education with strong extracurricular programs.' },
      { title: 'Elms Park & Recreation', desc: 'Community parks with playgrounds, sports facilities, and seasonal programming for families.' },
      { title: 'Miller Road Corridor', desc: 'Nearby shopping, dining, and services along one of the busiest commercial corridors in Genesee County.' },
      { title: 'Heritage Festival', desc: 'An annual community celebration featuring parades, live music, food vendors, and family activities.' }
    ],
    marketStats: { medianPrice: '$175,000', daysOnMarket: '25', popularTypes: 'Single Family, Ranch, Bi-Level' },
    buyingContent: 'Swartz Creek offers buyers a welcoming community with strong schools and affordable housing. The market features well-maintained homes in quiet subdivisions, many with updated interiors and spacious yards. David Hamady helps buyers find the right home in Swartz Creek, leveraging his local expertise to identify opportunities before they hit the broader market.',
    sellingContent: 'Homes in Swartz Creek attract families and professionals looking for quality living at an accessible price point. David Hamady\'s marketing approach combines professional photography, strategic pricing, and targeted digital campaigns to generate strong interest and competitive offers for your Swartz Creek property.'
  },
  'oakland-county': {
    name: 'Oakland County',
    state: 'MI',
    zip: '48301',
    county: 'Oakland',
    image: 'https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic_flint_home_002.jpeg',
    heroSubtitle: 'Premier Living in Southeast Michigan',
    description: 'Oakland County is one of the most prosperous counties in the United States and a premier destination for homebuyers seeking quality schools, vibrant communities, and strong property values. From the upscale communities of Bloomfield Hills and Birmingham to the family-friendly neighborhoods of Pontiac and Waterford, Oakland County offers an exceptional range of real estate opportunities across its diverse cities and townships.',
    sellingPoints: [
      'Consistently ranked among the wealthiest and most desirable counties in Michigan',
      'Top-rated school districts including Birmingham, Bloomfield Hills, and Clarkston',
      'Thriving commercial corridors and proximity to major Detroit metro employers',
      'Abundant parks, lakes, and recreational amenities throughout the county'
    ],
    amenities: [
      { title: 'Cranbrook Educational Community', desc: 'A world-renowned arts and education campus in Bloomfield Hills featuring museums, gardens, and acclaimed schools.' },
      { title: 'Oakland County Parks', desc: 'Over 13 parks spanning thousands of acres with trails, beaches, golf courses, and year-round programming.' },
      { title: 'Somerset Collection', desc: 'One of Michigan\'s premier luxury shopping destinations located in Troy, anchoring a vibrant retail and dining corridor.' },
      { title: 'Proximity to Detroit Metro', desc: 'Easy access to Detroit, DTW Airport, and major employment centers via I-75, I-696, and M-59.' }
    ],
    marketStats: { medianPrice: '$320,000', daysOnMarket: '20', popularTypes: 'Single Family, Luxury, New Construction' },
    buyingContent: 'Oakland County offers buyers an exceptional combination of strong schools, safe communities, and long-term property value appreciation. Whether you are searching for a luxury estate in Bloomfield Hills, a family home in Clarkston, or an investment property in Pontiac, David Hamady brings the market knowledge and negotiation expertise to help you secure the right property at the right price.',
    sellingContent: 'Oakland County\'s strong buyer demand and limited inventory create favorable conditions for sellers. David Hamady leverages professional marketing, accurate pricing strategy, and broad digital exposure to position your Oakland County home for maximum visibility and competitive offers. His financial background ensures your transaction is handled with precision from listing to closing.'
  }
};

const NeighborhoodPage = () => {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyImages, setPropertyImages] = useState({});

  const neighborhood = NEIGHBORHOODS[citySlug];

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode, citySlug]);

  useEffect(() => {
    if (!neighborhood) return;
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?${neighborhood.county ? `county=${neighborhood.county}` : neighborhood.city ? `city=${neighborhood.city}` : `zip=${neighborhood.zip}`}&state=${SITE_CONFIG.STATE}&propertyType=Residential&propertyType=Residential+Income&firstPhoto=true&hasMedia=true&limit=12`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          // Fetch all images first, then render
          const imageEntries = await Promise.all(props.map(async (property) => {
            if (property.first_photo_url) return [property.listing_key, property.first_photo_url];
            try {
              const mediaRes = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${property.listing_key}`, {});
              if (mediaRes.ok) {
                const mediaData = await mediaRes.json();
                const first = (mediaData.media || [])
                  .filter(m => m.media_url || m.s3_link)
                  .sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                if (first) return [property.listing_key, first.media_url || first.s3_link];
              }
            } catch (e) {}
            return [property.listing_key, null];
          }));
          setPropertyImages(Object.fromEntries(imageEntries.filter(([, url]) => url)));
          setProperties(props);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [neighborhood]);

  if (!neighborhood) {
    return (
      <div className="neighborhood-page">
        <div className="neighborhood-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h1>Neighborhood Not Found</h1>
          <button onClick={() => navigate('/')} className="nb-cta-button primary">Return Home</button>
        </div>
      </div>
    );
  }

  const pageTitle = `Homes for Sale in ${neighborhood.name}, ${neighborhood.state} | Buy & Sell with David Hamady`;
  const pageDescription = citySlug === 'oakland-county'
    ? `Looking to buy or sell a home in Oakland County, Michigan? David Hamady at HamadyRobinson, REALTORS® provides expert real estate services across Bloomfield Hills, Birmingham, Pontiac, Clarkston, Waterford, and all of Oakland County. Browse luxury and family listings, get a free home valuation, and work with a trusted local agent.`
    : `Looking to buy or sell a home in ${neighborhood.name}, Michigan? David Hamady at HamadyRobinson, REALTORS® provides expert real estate services in ${neighborhood.name} and Genesee County. Browse listings, get a free home valuation, and work with a trusted local agent.`;
  const pageKeywords = citySlug === 'oakland-county'
    ? `Oakland County homes for sale, Oakland County real estate, Bloomfield Hills homes, Birmingham MI real estate, Pontiac homes for sale, Clarkston MI homes, Waterford MI real estate, luxury homes Oakland County, David Hamady realtor Oakland County, buy home Oakland County MI, sell home Oakland County MI`
    : `${neighborhood.name} homes for sale, ${neighborhood.name} real estate, buy home ${neighborhood.name} MI, sell home ${neighborhood.name} MI, David Hamady realtor, ${neighborhood.name} Michigan realtor, Genesee County real estate`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "David Hamady - HamadyRobinson, REALTORS®",
    "url": `https://${SEO_CONFIG.DOMAIN}/neighborhood/${citySlug}`,
    "image": SEO_CONFIG.OG_IMAGE,
    "telephone": SITE_CONFIG.AGENT.PHONE,
    "email": SITE_CONFIG.AGENT.EMAIL,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": SEO_CONFIG.STREET_ADDRESS,
      "addressLocality": SEO_CONFIG.CITY,
      "addressRegion": SEO_CONFIG.STATE,
      "postalCode": SEO_CONFIG.ZIP_CODE,
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "City",
      "name": neighborhood.name,
      "containedInPlace": { "@type": "State", "name": "Michigan" }
    },
    "description": pageDescription
  };

  return (
    <div className="neighborhood-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={`https://${SEO_CONFIG.DOMAIN}/neighborhood/${citySlug}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={neighborhood.image} />
        <meta property="og:url" content={`https://${SEO_CONFIG.DOMAIN}/neighborhood/${citySlug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={neighborhood.image} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="nb-hero">
        <div className="nb-hero-overlay">
          <div className="nb-hero-text">
            <button onClick={() => navigate('/')} className="nb-back-btn">← Home</button>
            <div className="nb-hero-content">
              <p className="nb-hero-subtitle">{neighborhood.heroSubtitle}</p>
              <h1>Real Estate in {neighborhood.name}, Michigan</h1>
              <p className="nb-hero-agent">David Hamady — HamadyRobinson, REALTORS®</p>
            </div>
          </div>
          <div className="nb-hero-image-frame">
            <img src={neighborhood.image} alt={`${neighborhood.name} Michigan real estate`} />
          </div>
        </div>
      </div>

      <div className="nb-container">
        {/* About Section */}
        <section className="nb-section">
          <h2>About {neighborhood.name}</h2>
          <p>{neighborhood.description}</p>
          <div className="nb-highlights">
            {neighborhood.sellingPoints.map((point, i) => (
              <div key={i} className="nb-highlight-item">
                <span className="nb-check">✓</span>
                <span>{point}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Buying Section */}
        <section className="nb-section nb-buying">
          <h2>Buying a Home in {neighborhood.name}</h2>
          <p>{neighborhood.buyingContent}</p>
          <div className="nb-inline-cta">
            <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}?subject=Buying Inquiry - ${neighborhood.name}`} className="nb-cta-button primary">
              Contact David About Buying
            </a>
          </div>
        </section>

        {/* Selling Section */}
        <section className="nb-section nb-selling">
          <h2>Selling Your Home in {neighborhood.name}</h2>
          <p>{neighborhood.sellingContent}</p>
          <div className="nb-inline-cta">
            <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}?subject=Free Home Valuation - ${neighborhood.name}`} className="nb-cta-button primary">
              Get a Free Home Valuation
            </a>
          </div>
        </section>

        {/* Amenities */}
        <section className="nb-section">
          <h2>Living in {neighborhood.name}: Local Highlights</h2>
          <div className="nb-amenities-grid">
            {neighborhood.amenities.map((a, i) => (
              <div key={i} className="nb-amenity-card">
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Market Stats */}
        <section className="nb-section">
          <h2>{neighborhood.name} Real Estate Market Overview</h2>
          <div className="nb-stats-grid">
            <div className="nb-stat-card">
              <span className="nb-stat-value">{neighborhood.marketStats.medianPrice}</span>
              <span className="nb-stat-label">Median Home Price</span>
            </div>
            <div className="nb-stat-card">
              <span className="nb-stat-value">{neighborhood.marketStats.daysOnMarket} Days</span>
              <span className="nb-stat-label">Avg. Days on Market</span>
            </div>
            <div className="nb-stat-card">
              <span className="nb-stat-value">{neighborhood.marketStats.popularTypes}</span>
              <span className="nb-stat-label">Popular Property Types</span>
            </div>
          </div>
        </section>

        {/* Properties */}
        <section className="nb-section">
          <h2>Homes for Sale in {neighborhood.name}</h2>
          {loading ? (
            <p className="nb-loading">Loading available properties...</p>
          ) : properties.length > 0 ? (
            <div className="nb-properties-grid">
              {properties.slice(0, 12).map((property) => (
                <div
                  key={property.id || property.listing_key}
                  className="nb-property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                >
                  {propertyImages[property.listing_key] && (
                    <img
                      src={propertyImages[property.listing_key]}
                      alt={formatStreetAddress(property)}
                      className="nb-property-card-img"
                    />
                  )}
                  <div className="nb-property-info">
                    <span className="nb-property-price">${(property.list_price || property.current_price)?.toLocaleString()}</span>
                    <span className="nb-property-address">{formatPropertyAddress(property)}</span>
                    {property.property_sub_type && <span className="nb-property-type">{property.property_sub_type}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="nb-loading">No active listings found. Contact David for off-market opportunities.</p>
          )}
        </section>

        {/* CTA */}
        <section className="nb-cta-section">
          <h2>Work with David Hamady in {neighborhood.name}</h2>
          <p>Whether you're buying your first home, upgrading, or selling your current property, David Hamady provides the local expertise and personalized service you deserve.</p>
          <div className="nb-cta-buttons">
            <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}?subject=Real Estate Inquiry - ${neighborhood.name}`} className="nb-cta-button primary">
              Email David
            </a>
            <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`} className="nb-cta-button secondary">
              Call {SITE_CONFIG.AGENT.PHONE}
            </a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="nb-footer">
        <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} className="nb-mls-logo" />
        <span className="nb-disclaimer">{SITE_CONFIG.MLS.DISCLAIMER} | Brokerage License: {SITE_CONFIG.AGENT.BROKERAGE_LICENSE} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
      </div>
    </div>
  );
};

export default NeighborhoodPage;
